import { useMemo, useState } from "react";

import { InfinityToggle } from "../../../components/InfinityToggle/InfinityToggle";
import { Input } from "../../../components/Input/Input";
import {
  MetaSchemaEditor,
  type MetaSchemaField,
} from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import { SCHEMA_KEY_PATTERN } from "../../../components/MetaSchemaEditor/metaSchema";
import { Select, type Option } from "../../../components/Select/Select";
import type { HelpClaimMode, HelpRequestType } from "./types";
import { humanizeDuration } from "./utils";
import { parseReward, rewardToLine, sameReward } from "../../../lib/reward";
import {
  AMOUNT_MAX,
  AMOUNT_MIN,
  MAX_MAX,
  MAX_MIN,
  TTL_MAX,
  TTL_MIN,
  TYPE_REGEX,
  WINDOW_MAX,
  WINDOW_MIN,
  amountError,
  donorRewardError,
  limitError,
  payloadSchemaHeadError,
  payloadSchemaProblem,
  ttlError,
} from "./helpTypes";
import styles from "./HelpTypesEditor.module.css";

/**
 * What the small line under a row's ttl box says: how long this type's requests
 * live, and — when the box is empty — that the number came from the section's
 * Default TTL rather than from the row.
 *
 * `0` is "never expires" on both sides of that, which is why it is read before
 * `humanizeDuration` ever sees it: that function would say "0 seconds", the one
 * reading a duration of zero does NOT have here.
 *
 * `null` while Default TTL is itself empty, which makes it NaN: this line's
 * whole job is to name the value a type actually runs on, and a dash would read
 * as a configured one. The state is transient — Save is blocked on that field's
 * own "Required" meanwhile.
 */
function ttlNote(
  ttlSeconds: number | undefined,
  defaultTtlSeconds: number,
): string | null {
  if (ttlSeconds === 0) return "never expires";
  if (ttlSeconds !== undefined) return `= ${humanizeDuration(ttlSeconds)}`;
  if (!Number.isFinite(defaultTtlSeconds)) return null;
  if (defaultTtlSeconds === 0) return "= never (default)";
  return `= ${humanizeDuration(defaultTtlSeconds)} (default)`;
}

/** "One at a time" leads, as in the helper line under the editor. */
const CLAIM_OPTIONS: Option[] = [
  { value: "one_at_a_time", label: "One at a time" },
  { value: "all_at_once", label: "All at once" },
];

interface Props {
  value: HelpRequestType[];
  onChange: (next: HelpRequestType[]) => void;
  /** The section's `default_ttl_seconds`, read out under a row that sets no
   *  `ttl` of its own — the value that type actually runs on. */
  defaultTtlSeconds: number;
}

/**
 * `help_request.types` — one row per request type: its name, the mode by which
 * the player takes what was donated to it, how often they may ask, how much one
 * request may gather, how long it stays open, and what a donor gets back for
 * helping.
 *
 * Mockup: .codelane/artifacts/t-29/config-help-claim.html L265-L307, extended
 * by .codelane/artifacts/t-58/config-help-limits.html L294-L375, then by
 * .codelane/artifacts/t-81/config-help-max-amount.html L308-L411, then by
 * .codelane/artifacts/t-95/config-help-donor-reward.html L330-L423. A table
 * rather than several lists because every one of these is a property OF the
 * type: a separate "types with a cap" list would be two things obliged to
 * agree, and a row makes disagreement inexpressible.
 *
 * Errors follow MetaSchemaEditor: the offending row colours its own box and
 * says why, while `helpTypesError` lifts the same rules to the save bar.
 */
export function HelpTypesEditor({ value, onChange, defaultTtlSeconds }: Props) {
  /**
   * What is actually in each reward box while it is being typed in, by row.
   *
   * The row itself holds a VALUE, so a box rendered from the row would be
   * rewritten under the caret the moment the text parses — type a space into
   * `{"coins":50}` and the box snaps back, the caret jumps to the end, and the
   * next character lands outside the document. Keeping the text here means the
   * box is never rewritten while its meaning has not changed.
   *
   * Keyed by row index, and stale entries need no cleanup: `sameReward` throws
   * a draft out the moment it stops meaning what its row holds, so the worst a
   * shifted index can do is show the same value with somebody else's spacing.
   */
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  /**
   * Which rows have their payload schema open, by row index — closed is the
   * default and the state a page loads in.
   *
   * Keyed by position, like `drafts` above, but unlike `drafts` it cannot be
   * checked against the row it belongs to: "open" is not a fact about the type,
   * so a stale entry would simply hand the caret to whichever row slid into
   * that index. `removeRow` shifts the map instead — a schema does not spring
   * open under the operator because they deleted the type above it.
   */
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  // Which names collide. Case-sensitive — TYPE_REGEX admits lowercase only.
  const duplicates = useMemo(() => {
    const seen = new Set<string>();
    const dups = new Set<string>();
    for (const row of value) {
      if (!row.type) continue;
      if (seen.has(row.type)) dups.add(row.type);
      seen.add(row.type);
    }
    return dups;
  }, [value]);

  const patch = (idx: number, next: Partial<HelpRequestType>) =>
    onChange(value.map((row, i) => (i === idx ? { ...row, ...next } : row)));

  /** Drop a row, and slide the open-schema flags down with it. */
  const removeRow = (idx: number) => {
    setExpanded((prev) => {
      const next: Record<number, boolean> = {};
      for (const [key, open] of Object.entries(prev)) {
        const i = Number(key);
        if (i === idx) continue;
        next[i > idx ? i - 1 : i] = open;
      }
      return next;
    });
    onChange(value.filter((_, i) => i !== idx));
  };

  type NumberKey =
    | "max_per_window"
    | "window_seconds"
    | "max_amount"
    | "ttl_seconds";

  /**
   * Write one of the row's optional numbers as a VALUE. `undefined` DELETES the
   * key rather than storing 0 or NaN: "not set" is the absence of the key — no
   * limit, no cap, or the section's own TTL — and a row that arrived without
   * one has to serialise back without it.
   */
  const patchValue = (idx: number, key: NumberKey, next: number | undefined) =>
    onChange(
      value.map((row, i) => {
        if (i !== idx) return row;
        const out = { ...row };
        if (next === undefined) delete out[key];
        else out[key] = next;
        return out;
      }),
    );

  /** The same write from what the operator TYPED: an emptied or half-typed box
   *  is "not set". The ∞ toggle goes through `patchValue` directly, since what
   *  it produces was never text. */
  const patchNumber = (idx: number, key: NumberKey, raw: string) => {
    const n = Number(raw);
    patchValue(idx, key, raw === "" || !Number.isFinite(n) ? undefined : n);
  };

  /**
   * Write the row's payload schema.
   *
   * An emptied list DELETES the key rather than storing `[]`: the two say the
   * same thing to the server — a type that declares nothing accepts any payload
   * — and absent is what every project's config already says. Deleting the last
   * field therefore puts a type back exactly as it was before anybody declared
   * one, rather than leaving an empty array behind in the saved config.
   */
  const patchSchema = (idx: number, next: MetaSchemaField[]) =>
    onChange(
      value.map((row, i) => {
        if (i !== idx) return row;
        const out = { ...row };
        if (next.length === 0) delete out.payload_schema;
        else out.payload_schema = next;
        return out;
      }),
    );

  /**
   * Write the row's donor reward from what the operator typed.
   *
   * An emptied box deletes the key, like every optional setting above it: no
   * reward is the absence of one. Text that IS a reward is stored parsed — it
   * is a value in the config, not a string, and it goes on the wire as one.
   *
   * Text that is not a reward yet is stored AS TEXT, and that is the point: the
   * row keeps what the operator typed instead of losing it, the box paints
   * itself red, and `helpTypesError` blocks Save on the same string until it
   * becomes a reward or goes away. Nothing else can put a string in this field,
   * so nothing else is mistaken for one.
   */
  const patchReward = (idx: number, raw: string) => {
    // What they typed, kept verbatim for the box; the row gets the meaning.
    setDrafts((prev) => ({ ...prev, [idx]: raw }));
    onChange(
      value.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row };
        const parsed = parseReward(raw);
        if (!parsed.ok) next.donor_reward = raw;
        else if (parsed.value === undefined) delete next.donor_reward;
        else next.donor_reward = parsed.value;
        return next;
      }),
    );
  };

  return (
    <>
      <div className={styles.gridEditor}>
        <div className={styles.scroller}>
          <div className={`${styles.row} ${styles.types} ${styles.head}`}>
            <div>request_type</div>
            <div>claim mode</div>
            <div>max</div>
            <div>per window</div>
            <div>max amount</div>
            <div>ttl</div>
            <div />
          </div>
          {value.map((row, idx) => {
            // A row the operator has not named yet is not yet a mistake — the
            // save bar still refuses it, but the box stays quiet.
            const name = row.type || `type ${idx + 1}`;
            const error = !row.type
              ? undefined
              : duplicates.has(row.type)
                ? "Duplicate type"
                : !TYPE_REGEX.test(row.type)
                  ? "Use lowercase letters, digits, underscore"
                  : undefined;
            const limit = limitError(row);
            // Half a pair marks BOTH boxes: neither half means anything on its
            // own, so neither may look like the settled one.
            const maxBad = !!(limit?.pair || limit?.max);
            const windowBad = !!(limit?.pair || limit?.window);
            const windowNote = limit?.pair ?? limit?.window;
            const amountBad = amountError(row);
            const ttlBad = ttlError(row);
            // ∞ is the value itself, so nothing has to be kept in step with it.
            const ttlNever = row.ttl_seconds === 0;
            const ttlLine = ttlNote(row.ttl_seconds, defaultTtlSeconds);
            const rewardBad = donorRewardError(row);
            const schemaBad = payloadSchemaProblem(row);
            const draft = drafts[idx];
            const rewardText =
              draft !== undefined && sameReward(draft, row.donor_reward)
                ? draft
                : rewardToLine(row.donor_reward);
            return (
              <div
                key={idx}
                className={`${styles.row} ${styles.types} ${styles.tall}`}
              >
                <div className={styles.cell}>
                  <Input
                    className={styles.monoInput}
                    value={row.type}
                    onChange={(e) => patch(idx, { type: e.target.value })}
                    placeholder="request_type"
                    aria-label={`Type ${idx + 1} name`}
                    error={error}
                  />
                </div>
                <div className={styles.cell}>
                  <Select
                    className={styles.claimSelect}
                    value={row.claim}
                    onChange={(v) => patch(idx, { claim: v as HelpClaimMode })}
                    options={CLAIM_OPTIONS}
                    aria-label={`${name} claim mode`}
                  />
                </div>
                <div className={styles.cell}>
                  <Input
                    className={maxBad ? styles.err : undefined}
                    type="number"
                    min={MAX_MIN}
                    max={MAX_MAX}
                    value={row.max_per_window ?? ""}
                    onChange={(e) =>
                      patchNumber(idx, "max_per_window", e.target.value)
                    }
                    placeholder="∞"
                    aria-label={`${name} max per window`}
                    aria-invalid={maxBad || undefined}
                  />
                  {limit?.max && (
                    <div className={styles.errText}>{limit.max}</div>
                  )}
                </div>
                <div className={styles.cell}>
                  <div className={styles.unitWrap}>
                    <Input
                      className={`${styles.unitInput} ${windowBad ? styles.err : ""}`}
                      type="number"
                      min={WINDOW_MIN}
                      max={WINDOW_MAX}
                      value={row.window_seconds ?? ""}
                      onChange={(e) =>
                        patchNumber(idx, "window_seconds", e.target.value)
                      }
                      placeholder="—"
                      aria-label={`${name} window seconds`}
                      aria-invalid={windowBad || undefined}
                      trailingIcon={
                        <span className={styles.unit} aria-hidden>
                          sec
                        </span>
                      }
                    />
                  </div>
                  {windowNote ? (
                    <div className={styles.errText}>{windowNote}</div>
                  ) : (
                    <div className={styles.mini}>
                      {/* The same `humanizeDuration` that reads out Default TTL
                          and Unclaimed lifetime, so one section spells a
                          duration one way (mockup L469-L472). */}
                      {row.window_seconds === undefined
                        ? "No limit"
                        : `= ${humanizeDuration(row.window_seconds)}`}
                    </div>
                  )}
                </div>
                <div className={styles.cell}>
                  <Input
                    className={amountBad ? styles.err : undefined}
                    type="number"
                    min={AMOUNT_MIN}
                    max={AMOUNT_MAX}
                    value={row.max_amount ?? ""}
                    onChange={(e) =>
                      patchNumber(idx, "max_amount", e.target.value)
                    }
                    // The same ∞ the quota's max wears when it is empty: both
                    // boxes mean "nothing stops this" when nothing is in them.
                    placeholder="∞"
                    aria-label={`${name} max amount`}
                    aria-invalid={!!amountBad || undefined}
                  />
                  {amountBad && (
                    <div className={styles.errText}>{amountBad}</div>
                  )}
                </div>
                <div className={styles.cell}>
                  <div className={`${styles.unitWrap} ${styles.hasNever}`}>
                    <Input
                      className={[
                        styles.unitInput,
                        // The box makes room for the unit AND the button, or —
                        // once it says «Never» — for the button alone.
                        ttlNever ? styles.neverInput : styles.withNever,
                        ttlBad ? styles.err : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      // A word is not a number: to say «Never» the box has to
                      // become a text one, and it goes read-only rather than
                      // disabled so a keyboard still reaches the toggle.
                      type={ttlNever ? "text" : "number"}
                      min={ttlNever ? undefined : TTL_MIN}
                      max={ttlNever ? undefined : TTL_MAX}
                      value={ttlNever ? "Never" : (row.ttl_seconds ?? "")}
                      readOnly={ttlNever}
                      onChange={(e) =>
                        patchNumber(idx, "ttl_seconds", e.target.value)
                      }
                      placeholder="—"
                      aria-label={`${name} ttl seconds`}
                      aria-invalid={!!ttlBad || undefined}
                      trailingIcon={
                        ttlNever ? undefined : (
                          <span className={styles.unit} aria-hidden>
                            sec
                          </span>
                        )
                      }
                    />
                    {/* No `fallback`: an empty ttl is itself a state here, so a
                        row that has no number to go back to returns to running
                        on the section's Default TTL.

                        The toggle remembers the number it replaced, and that
                        memory is keyed by position exactly as `drafts` above
                        is: rows render by index, so removing one hands its
                        remembered ttl to whichever row lands on that index.
                        The cost is the same — one number to retype — and the
                        cure is the same stable row id neither has. */}
                    <InfinityToggle
                      value={row.ttl_seconds}
                      onChange={(next) => patchValue(idx, "ttl_seconds", next)}
                      fieldLabel={`${name} ttl`}
                      className={styles.infNarrow}
                    />
                  </div>
                  {/* An empty box is not "no TTL" — it is the section's
                      default, which is what this request will really run on.
                      Reading it out here is the difference between a blank cell
                      and a known answer (mockup L349). */}
                  {ttlBad ? (
                    <div className={styles.errText}>{ttlBad}</div>
                  ) : (
                    ttlLine && <div className={styles.mini}>{ttlLine}</div>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.rowBtn}
                  aria-label={`Remove ${name}`}
                  onClick={() => removeRow(idx)}
                >
                  ✕
                </button>
                {/* The reward is a property OF the type like everything left
                    of it, but it is the one whose value is a whole JSON
                    document — so it takes the row's full width on a second
                    line instead of an eighth column that would set every
                    project's table scrolling sideways (mockup L344-L350,
                    L533-L551). */}
                <div className={styles.subLine}>
                  <div className={styles.subLabel}>donor reward</div>
                  <div className={styles.subField}>
                    <Input
                      className={`${styles.monoInput} ${rewardBad ? styles.err : ""}`}
                      value={rewardText}
                      onChange={(e) => patchReward(idx, e.target.value)}
                      placeholder="—"
                      aria-label={`${name} donor reward`}
                      aria-invalid={!!rewardBad || undefined}
                    />
                    {/* An empty box is a state, not a gap: this type pays its
                        donors nothing, and the line says so rather than
                        leaving the operator to guess (mockup L396). */}
                    {rewardBad ? (
                      <div className={styles.errText}>{rewardBad}</div>
                    ) : (
                      row.donor_reward === undefined && (
                        <div className={styles.mini}>No reward</div>
                      )
                    )}
                  </div>
                </div>
                {/* The schema is the row's second full-width sub-line, and the
                    one that is COLLAPSED by default: an always-open list of
                    fields adds some 200px to every row of a table every project
                    has, most of which declare no schema at all (mockup
                    L415-L463, L485-L498). */}
                <div className={styles.subLine}>
                  <div className={styles.subLabel}>payload schema</div>
                  <div className={styles.subField}>
                    <PayloadSchema
                      name={name}
                      value={row.payload_schema}
                      problem={schemaBad}
                      expanded={!!expanded[idx]}
                      onToggle={() =>
                        setExpanded((prev) => ({
                          ...prev,
                          [idx]: !prev[idx],
                        }))
                      }
                      onChange={(next) => patchSchema(idx, next)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.addRow}
          onClick={() =>
            onChange([...value, { type: "", claim: "all_at_once" }])
          }
        >
          + Add type
        </button>
      </div>
      <div className={styles.helper}>
        <b>One at a time</b> — the player may take part of a donation and leave
        the rest for later (lives against a cap). <b>All at once</b> — a
        donation is taken whole, in one call.
      </div>
      <div className={styles.helper}>
        <b>max / per window</b> — how many requests of this type a player may
        create over the last window, counted back from now. Leave both empty
        for no limit. Every request created counts, including one that was
        cancelled or has expired.
      </div>
      <div className={styles.helper}>
        <b>max amount</b> — how much one request of this type may gather in
        total. It is fulfilled once donations add up to it. Leave empty for no
        cap: the request then stays open until it expires, or forever if its
        ttl is ∞.
      </div>
      <div className={styles.helper}>
        <b>ttl</b> — how long a request of this type stays open. Leave empty to
        use the section default below. Press ∞ for a request that never expires
        — it closes only when it is fulfilled or cancelled.
      </div>
      <div className={styles.helper}>
        <b>donor reward</b> — what a player gets back for donating to a request
        of this type, the same for every donation. A JSON object{" "}
        <code>{'{"coins": 50}'}</code> or a list of items{" "}
        <code>{'[{"currency": "Coins", "amount": 600}]'}</code>; the platform
        never reads inside it. Leave empty for no reward. It is handed to the
        donor in the response to their donation — nothing is stored, so the game
        credits it there and then.
      </div>
      <div className={styles.helper}>
        <b>payload schema</b> — the fields a request of this type has to carry,
        so “ask for a card” can be “ask for <code>ace_fire</code>
        ”. A type with no schema behaves exactly as it does today: whatever the
        game sends is stored and echoed back, and a request with no payload at
        all is fine. Declaring the first field is what turns that off — from
        then on the type accepts only what is listed here, and a request that
        breaks the schema is refused with <code>help_invalid_payload</code>.
      </div>
    </>
  );
}

/**
 * One type's `payload_schema`, collapsed to a line that says everything it
 * declares and opened only to edit it.
 *
 * The open half is `MetaSchemaEditor` unchanged — this is the same
 * `{key, type, allowed}` declaration `profile.meta_schema` is written in, read
 * by the same package on the server — with the one box that schema has no use
 * for: `required`.
 *
 * Mockup: .codelane/artifacts/t-124/config-help-payload-schema.html L415-L463
 * (open), L485-L498 (collapsed), L520-L529 (no schema), L569-L602 (the three
 * things the collapsed head says).
 */
function PayloadSchema({
  name,
  value,
  problem,
  expanded,
  onToggle,
  onChange,
}: {
  /** The type this schema belongs to, for the controls' accessible names. */
  name: string;
  /** Absent for a type that declares no schema — which is not the same as an
   *  empty one to a reader, though it is to the server. */
  value: MetaSchemaField[] | undefined;
  problem: ReturnType<typeof payloadSchemaProblem>;
  expanded: boolean;
  onToggle: () => void;
  onChange: (next: MetaSchemaField[]) => void;
}) {
  // Memoised so the chip pass below does not re-run on every keystroke in a
  // neighbouring cell: `value ?? []` is a fresh array each render otherwise.
  const fields = useMemo(() => value ?? [], [value]);
  // Which chips to redden: a key that is not a key, and every repeat AFTER the
  // first — the first occurrence is not the mistake, the second one is (mockup
  // L593-L594).
  const badChips = useMemo(() => {
    const seen = new Set<string>();
    const bad = new Set<number>();
    fields.forEach((f, i) => {
      if (!f.key || !SCHEMA_KEY_PATTERN.test(f.key) || seen.has(f.key))
        bad.add(i);
      if (f.key) seen.add(f.key);
    });
    return bad;
  }, [fields]);

  return (
    <>
      <button
        type="button"
        className={`${styles.schemaHead} ${problem ? styles.schemaHeadErr : ""}`}
        aria-expanded={expanded}
        aria-label={`${name} payload schema`}
        onClick={onToggle}
      >
        <span className={styles.caret} aria-hidden>
          {expanded ? "▾" : "▸"}
        </span>
        <span className={styles.summary}>
          {fields.length === 0 ? (
            <span className={styles.none}>
              No schema — any payload is accepted
            </span>
          ) : (
            fields.map((f, i) => (
              <span
                key={i}
                className={`${styles.fkey} ${badChips.has(i) ? styles.fkeyErr : ""}`}
              >
                {f.key || "—"}
                {f.required && (
                  <span className={styles.req} aria-label="required">
                    *
                  </span>
                )}{" "}
                <span className={styles.ftype}>{f.type}</span>
              </span>
            ))
          )}
        </span>
        <span className={`${styles.count} ${problem ? styles.countErr : ""}`}>
          {problem
            ? payloadSchemaHeadError(problem)
            : `${fields.length} ${fields.length === 1 ? "field" : "fields"}`}
        </span>
      </button>
      {expanded && (
        <MetaSchemaEditor value={fields} onChange={onChange} showRequired />
      )}
    </>
  );
}
